import { useState, useEffect, useCallback } from 'react';
import type { CheckInHistoryItem } from '../types/checkIns';
import { useApiClientsOrNull } from '../context/ApiClientsContext';

interface UseFactorCheckInHistoryOptions {
  limit?: number;
}

interface UseFactorCheckInHistoryResult {
  checkIns: CheckInHistoryItem[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useFactorCheckInHistory(
  factorId: string,
  sphereId: string,
  options: UseFactorCheckInHistoryOptions = {}
): UseFactorCheckInHistoryResult {
  const { limit = 20 } = options;
  const apiClients = useApiClientsOrNull();
  const [checkIns, setCheckIns] = useState<CheckInHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!apiClients?.graphClient || !factorId || !sphereId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClients.graphClient.get(
        `/sphere/${sphereId}/factors/${factorId}/checkins?limit=${limit}`
      );
      setCheckIns(resp?.checkIns ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [apiClients, factorId, sphereId, limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return { checkIns, loading, error, refresh: fetchHistory };
}
